/* eslint-disable @typescript-eslint/member-ordering */
import { ISelectOption } from '@aon/aon-tws-common-ui';
import CommonConstants from '@aon/aon-tws-common-ui/build/lib/constants';
import { ResponseOptionGroupType } from '@aon/tws-common-ui-questions/build/lib/models/enum';
import ContentConstants from './contentConstants';
import { QuestionType } from './models';

export const getStringFromConfig = (value: string) => {
	return process.env[value] || 'ERROR!ERROR!';
};

export enum AppEnvironment {
	PROD,
	UAT,
	QA,
	CI,
	LOCAL,
}

export default class Constants {
	// Use .env or .env.local to override REACT_APP_API_BASE_URL parameter
	public static API_BASE_URL: string = getStringFromConfig('REACT_APP_API_BASE_URL');
	public static DOMAIN_NAME: string =
		Constants.API_BASE_URL !== 'ERROR!ERROR!' && Constants.API_BASE_URL !== 'baseUrl/' ? new URL(Constants.API_BASE_URL).hostname : '';
	private static readonly API_VERSION: string = 'v1';
	public static readonly API_URL: string = Constants.API_BASE_URL + Constants.API_VERSION;
	public static readonly SURVEY_API_URL = Constants.API_URL + '/surveys/{surveyId}';

	public static readonly SESSION_TIMEOUT: number = +(process.env.REACT_APP_SESSION_TIMEOUT
		? getStringFromConfig('REACT_APP_SESSION_TIMEOUT')
		: 1800);

	public static readonly API_CLIENT_LOGOUT_URL: string = Constants.API_URL + '/logout';

	// SIGNALR
	public static readonly SURVEYBUILDER_API_SIGNALR_URL: string = Constants.API_BASE_URL + 'notificationhub';

	// EXTERNAL SERVICES
	public static readonly USER_MANAGEMENT_API_URL: string = getStringFromConfig('REACT_APP_USER_MANAGEMENT_API_URL');
	public static readonly IDENTITY_SERVER_URL: string = getStringFromConfig('REACT_APP_IDENTITY_SERVER_URL');
	public static readonly ORGMANAGER_URL: string = getStringFromConfig('REACT_APP_ORGMANAGER_URL');
	public static readonly SURVEYBUILDER_URL: string = getStringFromConfig('REACT_APP_SURVEYBUILDER_URL');
	public static readonly QUESTIONLIBRARY_URL: string = getStringFromConfig('REACT_APP_QUESTIONLIBRARY_URL');
	public static readonly ADMINPORTAL_URL: string = getStringFromConfig('REACT_APP_ADMINPORTAL_URL');
	public static readonly MASSMAIL_URL: string = getStringFromConfig('REACT_APP_MASSMAIL_URL');
	public static readonly MONITORING_URL: string = getStringFromConfig('REACT_APP_MONITORING_URL');
	public static readonly MSUITE_BASE_URL: string = getStringFromConfig('REACT_APP_MSUITE_BASE_URL');
	public static readonly MRESEARCH_SURVEY_ENGINE_BASE_URL: string = getStringFromConfig('REACT_APP_MRESEARCH_SURVEY_ENGINE_BASE_URL');

	// ENVIRONMENT INFORMATION
	// ENVIRONMENT INFORMATION
	public static get APP_ENVIRONMENT(): AppEnvironment | string {
		const env = getStringFromConfig('REACT_APP_APPENVIRONMENT');

		switch (env.toUpperCase()) {
			case 'PROD':
				return AppEnvironment.PROD;
			case 'UAT':
				return AppEnvironment.UAT;
			case 'QA':
				return AppEnvironment.QA;
			case 'CI':
				return AppEnvironment.CI;
			case 'LOCAL':
				return AppEnvironment.LOCAL;
		}

		// If the environment could not be bound to a corresponding enum, return the raw environment value.
		return env;
	}
	public static readonly BRANCH_ENVIRONMENT: string = getStringFromConfig('REACT_APP_BRANCHENVIRONMENT');
	public static readonly APP_RELEASE_NUMBER: string = getStringFromConfig('REACT_APP_RELEASENUMBER');
	public static readonly IS_LLC = Constants.APP_ENVIRONMENT !== AppEnvironment.PROD;
	public static readonly GA_TRACKINGID = Constants.IS_LLC ? '' : 'UA-5522905-9';

	// CLIENT
	public static readonly API_CLIENT_EXTERNAL_APPLICATION_TYPES_URL = Constants.API_URL + '/client/{clientId}/external-application-types';
	public static readonly API_CLIENT_MIN_N_URL: string = Constants.API_URL + '/client/{clientId}/min-n';

	// SURVEYS
	public static readonly SURVEY_BUILDER_SURVEYS_API_URL: string = Constants.API_URL + '/surveys';
	public static readonly API_SURVEY_STATUS_LOGS_URL: string = `${Constants.SURVEY_BUILDER_SURVEYS_API_URL}/{surveyId}/status/logs`;
	public static readonly API_SURVEY_EDIT_MODEL_URL: string = Constants.API_URL + '/SurveyEditModels';
	public static readonly API_SURVEY_QUESTION_EDIT_MODELS_URL = `${Constants.API_SURVEY_EDIT_MODEL_URL}/{surveyId}/SurveyQuestionEditModels`;
	public static readonly API_SURVEY_LIST_URL: string = Constants.API_URL + '/survey-list-items';
	public static readonly API_SURVEYCOLUMNS_URL: string = Constants.API_URL + '/SurveyColumns';
	public static readonly API_SURVEYDEFINITIONS_URL: string = Constants.API_URL + '/SurveyDefinitions';
	public static readonly API_SURVEYSUMMARYITEMS_URL: string = Constants.API_URL + '/SurveySummaryItems';
	public static readonly API_SURVEYADMINSUMMARIES_URL = Constants.API_URL + '/SurveyAdminSummaries';
	public static readonly API_EXTERNAL_ATTRIBUTES: string = Constants.SURVEY_API_URL + '/external-attributes';
	public static readonly API_DISPLAY_LOGIC: string = Constants.API_URL + '/survey/{surveyId}/display-logic';
	public static readonly API_SURVEY_TEMPLATES: string = Constants.API_URL + '/survey-templates';
	public static readonly SURVEY_THEME_API_URL: string = `${Constants.SURVEY_API_URL}/theme`;
	public static readonly API_SURVEY_BRIDGE: string = Constants.API_URL + '/survey-bridges/internal/{surveyId}';
	public static readonly SURVEY_DEFINITION_TERM_CHARACTER_LIMIT = 100;
	public static readonly SURVEY_DEFINITION_DESCRIPTION_CHARACTER_LIMIT = 1000;
	public static readonly SURVEY_CONTENT_ITEM_SORT_DEFAULT = 'PrivacyPolicy|Instructions|Definition';

	// API ERROR MESSAGES
	public static readonly API_EXTERNAL_APPLICATION_TYPES_ERROR: string =
		'An error occurred while trying to fetch external application types.';

	// IMPORT RETRY
	public static readonly API_IMPORT_RETRY = Constants.SURVEY_API_URL + '/administration/reimport?mSuiteDatasetId={mSuiteDatasetId}';

	// PARTICIPANTS / AUDIENCE
	public static readonly AUDIENCES_API_BASE_URL: string = Constants.SURVEY_BUILDER_SURVEYS_API_URL + '/{surveyId}/audiences';
	public static readonly AUDIENCES_API_BASE_PAGED_URL: string =
		Constants.AUDIENCES_API_BASE_URL + '?offset={offset}&limit={limit}{searchText}';
	public static readonly AUDIENCE_API_BASE_URL: string = Constants.AUDIENCES_API_BASE_URL + '/{audienceId}';
	public static readonly AUDIENCE_COPY_API_BASE_URL: string =
		Constants.AUDIENCES_API_BASE_URL + '/{audienceId}/copy?isTestAudience={isTestAudience}';
	public static readonly AUDIENCE_PARTICIPATION_COUNTS_API_URL: string = '/participation-counts';
	public static readonly AUDIENCE_DISTRIBUTION_ATTRIBUTES_API_URL: string = '/person-attribute-names?clientId=';

	public static readonly API_TEXTTOSPEECH_CULTURE_URL: string = Constants.API_URL + '/text-to-speech-culture/supported';
	public static readonly API_TREND_GROUPS_URL: string = Constants.API_URL + '/{surveyId}/trend-groups';

	public static readonly THEMES_API_URL: string = `${Constants.API_URL}/themes`;
	// ROUTES
	public static readonly ROUTE_LANDING: string = '/landing';

	// SURVEY NOTES
	public static readonly API_SURVEYNOTES_URL = Constants.SURVEY_API_URL + '/notes';

	// EMAIL
	public static readonly API_EMAIL_URL: string = Constants.API_URL + '/email';
	public static readonly API_EMAILTEMPLATES_URL: string =
		Constants.SURVEY_BUILDER_SURVEYS_API_URL + '/{surveyId}/audiences/{audienceId}/emailtemplates';

	// CULTURES
	public static readonly API_CULTURES_URL: string = Constants.API_URL + '/Cultures';

	// TRANSLATIONS
	public static readonly API_TRANSLATIONS_URL: string = Constants.API_URL + '/TranslationEditItems';

	// PRIVACY POLICY
	public static readonly API_PRIVACY_POLICY_URL: string = Constants.SURVEY_API_URL + '/privacy-policy';

	// SURVEY TERMS/DEFINITIONS
	public static readonly API_SURVEY_TERMS_URL: string = Constants.SURVEY_API_URL + '/survey-terms';
	public static readonly API_SURVEY_TERM_ITEMS_URL: string = Constants.SURVEY_API_URL + '/survey-term-items';

	// BENCHMARKS
	private static BENCHMARK_LIBRARY_API_BASE_URL: string = getStringFromConfig('REACT_APP_NORM_LIBRARY_API_BASE_URL');
	private static readonly BENCHMARK_LIBRARY_API_VERSION = 'v1';
	private static readonly BENCHMARK_LIBRARY_API_URL: string =
		Constants.BENCHMARK_LIBRARY_API_BASE_URL + Constants.BENCHMARK_LIBRARY_API_VERSION;
	public static readonly BENCHMARK_LIBRARY_API_BENCHMARKS_URL: string = Constants.BENCHMARK_LIBRARY_API_URL + '/Norms';
	public static readonly BENCHMARK_LIBRARY_API_QUESTIONS_URL: string = Constants.BENCHMARK_LIBRARY_API_URL + '/Questions';
	public static readonly BENCHMARKS_API_GLOBALTOPQUARTILE: string = Constants.API_URL + '/benchmarks/global-top-quartile';
	public static readonly BENCHMARK_LIBRARY_API_CLIENT_BENCHMARKS_URL = Constants.BENCHMARK_LIBRARY_API_URL + '/clients/{clientId}/norms';

	// QUESTIONS
	private static QUESTION_LIBRARY_API_BASE_URL: string = getStringFromConfig('REACT_APP_QUESTION_LIBRARY_API_BASE_URL');
	private static readonly QUESTION_LIBRARY_API_VERSION = 'v1';
	private static readonly QUESTION_LIBRARY_API_URL: string =
		Constants.QUESTION_LIBRARY_API_BASE_URL + Constants.QUESTION_LIBRARY_API_VERSION;
	public static readonly QUESTION_LIBRARY_API_QUESTIONS_URL: string = Constants.QUESTION_LIBRARY_API_URL + '/Questions';
	public static readonly QUESTION_LIBRARY_API_VARIATIONS_URL: string = Constants.QUESTION_LIBRARY_API_URL + '/QuestionVariations';
	public static readonly QUESTION_LIBRARY_API_COLUMNS: string = Constants.QUESTION_LIBRARY_API_URL + '/Columns';
	public static readonly QUESTION_LIBRARY_API_TRANSLATIONS_URL: string = Constants.QUESTION_LIBRARY_API_URL + '/Translations';
	public static readonly QUESTION_LIBRARY_DIMENSIONS_API_URL: string = Constants.QUESTION_LIBRARY_API_URL + '/Dimensions';
	public static readonly QUESTION_LIBRARY_RESPONSEOPTIONS_API_URL: string = Constants.QUESTION_LIBRARY_API_URL + '/ResponseOptionGroups';
	public static readonly SURVEY_BUILDER_DIMENSIONS_API_URL = Constants.SURVEY_API_URL + '/survey-dimensions';
	public static readonly API_QUESTION_LIBRARY_EMAIL_TEMPLATES_LIST_URL: string = Constants.QUESTION_LIBRARY_API_URL + '/email-template';

	// QUESTION LIBRARY V2 APIs
	private static readonly API_V2_VERSION: string = 'v2';
	private static readonly V2_API_URL: string = Constants.QUESTION_LIBRARY_API_BASE_URL + Constants.API_V2_VERSION;
	public static readonly API_V2_QUESTIONS_URL: string = Constants.V2_API_URL + '/questions';
	public static readonly API_V2_QUESTION_VARIATIONS_URL: string = Constants.V2_API_URL + '/question-variations';
	public static readonly API_V2_OWNER_URL: string = Constants.V2_API_URL + '/owner';
	public static readonly API_V2_DIMENSIONS_URL: string = Constants.V2_API_URL + '/dimensions';
	public static readonly API_V2_DIMENSION_DETAILS_URL: string = Constants.V2_API_URL + '/dimensions/{dimensionId}';
	public static readonly API_V2_TAGS_URL: string = Constants.V2_API_URL + '/tags';
	public static readonly API_V2_TEMPLATES_URL: string = Constants.V2_API_URL + '/question-templates';
	public static readonly API_V2_RESPONSE_OPTION_GROUPS_URL: string = Constants.V2_API_URL + '/response-option-groups';
	public static readonly API_V2_QUESTION_TAGS_URL: string = Constants.V2_API_URL + '/questions/{questionId}/tags';
	public static readonly API_V2_QUESTION_ALLTAGS_URL: string = Constants.V2_API_URL + '/questions/tags';
	public static readonly API_V2_QUESTION_TEMPLATES_URL: string = Constants.V2_API_URL + '/questions/{questionId}/templates';
	public static readonly API_V2_QUESTION_ALLTEMPLATES_URL: string = Constants.V2_API_URL + '/questions/templates';
	public static readonly API_V2_QUESTION_DIMENSIONS_URL: string = Constants.V2_API_URL + '/questions/{questionId}/dimensions';
	public static readonly API_V2_QUESTION_VARIATION_URL: string = Constants.V2_API_URL + '/question-variations';

	// SURVEY IMAGE
	public static readonly API_SURVEY_IMAGE_URL = Constants.SURVEY_API_URL + '/image';

	// Question Changes
	public static readonly API_SURVEY_QUESTION_CHANGES_URL = Constants.SURVEY_API_URL + '/questions/changes';

	// Reporting Users
	public static readonly REPORTING_USERS_API_BASE: string = Constants.SURVEY_API_URL + '/reportingusers';
	public static readonly REPORTING_USERS_API_SAVE_USERS: string = Constants.REPORTING_USERS_API_BASE + '/save-users';
	public static readonly REPORTING_USERS_API_SEND_EMAIL: string = Constants.REPORTING_USERS_API_BASE + '/send-email';

	public static readonly HIERARCHY_REPORTING_ACCESS_CONFIGS_API = Constants.SURVEY_API_URL + '/hierarchy-reporting-access-configs';
	public static readonly REPORTING_PROFILE_CONFIGS_API = Constants.SURVEY_API_URL + '/reporting-profile-configs';

	// Users
	public static readonly USERS_FROM_CLIENT: string = Constants.USER_MANAGEMENT_API_URL + '/clients/{clientId}/users';

	// ROUTES
	public static readonly ROUTE_CONFIRMATION: string = '/confirmation';
	public static readonly ROUTE_BASICS: string = '/basics';
	public static readonly ROUTE_DEFINE_SURVEY_CONTENT: string = '/define-survey-content';
	public static readonly ROUTE_DEFINE_SURVEY_QUESTIONS: string = '/define-survey-questions';
	public static readonly ROUTE_MANAGE_SURVEY_DIMENSIONS: string = '/manage-survey-dimensions';
	public static readonly ROUTE_BRANDING_AND_THEME = '/branding-and-theme';
	public static readonly ROUTE_LOGIC_AND_LAYOUT: string = '/logic-and-layout';
	public static readonly ROUTE_CONFIRM_SCHEDULE: string = '/confirm-schedule';
	public static readonly ROUTE_COMPLETE: string = '/complete';
	public static readonly ROUTE_AUDIENCES: string = '/audiences';
	public static readonly ROUTE_AUDIENCE: string = '/audience';
	public static readonly ROUTE_SURVEY_ADMIN: string = '/survey-admin';
	public static readonly ROUTE_REPORTING: string = '/reporting-setup';

	public static readonly SUBROUTE_SURVEY_ADMIN_BENCHMARKS: string = 'benchmarks';
	public static readonly SUBROUTE_SURVEY_ADMIN_INVITE_EMAIL: string = 'invite-email';
	public static readonly SUBROUTE_SURVEY_ADMIN_OPEN_CLOSE: string = 'open-close';
	public static readonly SUBROUTE_SURVEY_ADMIN_REOPEN_SURVEY: string = 'survey-reopen';
	public static readonly SUBROUTE_SURVEY_ADMIN_PARTICIPANTS: string = 'participants';
	public static readonly SUBROUTE_SURVEY_ADMIN_RESET_REOPEN_RESEND: string = 'reset-reopen-resend';
	public static readonly SUBROUTE_SURVEY_ADMIN_PARTICIPATION_MONITOR = 'participation-monitor';
	public static readonly SUBROUTE_SURVEY_ADMIN_REMINDER_EMAIL: string = 'reminder-email';
	public static readonly SUBROUTE_SURVEY_ADMIN_SURVEY_OVERVIEW: string = 'survey-overview';
	public static readonly SUBROUTE_SURVEY_ADMIN_REPORTING_ACCESS: string = 'reporting-access';
	public static readonly SUBROUTE_SURVEY_ADMIN_TREND_TO: string = 'trend-to';
	public static readonly SUBROUTE_SURVEY_ADMIN_EDIT_SURVEY: string = 'edit-survey';
	public static readonly SUBROUTE_SURVEY_ADMIN_CANCEL_SURVEY: string = 'survey-cancel';
	public static readonly SUBROUTE_SURVEY_ADMIN_MANAGE_DIMENSIONS = 'manage-dimensions';
	public static readonly SUBROUTE_SURVEY_ADMIN_MANAGE_DIMENSIONS_ADD = 'new-dimension';
	public static readonly SUBROUTE_SURVEY_ADMIN_HIERARCHY_VIEW: string = 'hierarchy-view';
	public static readonly SUBROUTE_SURVEY_ADMIN_ANONYMITY_THRESHOLD: string = 'anonymity-threshold';
	public static readonly SUBROUTE_SURVEY_ADMIN_REPORTING_PROFILES: string = 'reporting-profiles';
	public static readonly SUBROUTE_SURVEY_ADMIN_AUDIENCE: string = 'audience';

	// ENUMS
	public static readonly QUESTION_TYPE_SCALE = 1;
	public static readonly QUESTION_TYPE_OPENENDED = 2;
	public static readonly QUESTION_TYPE_TEXTBOX = 4;
	public static readonly NON_DEMOGRAPHIC_QUESTION_TYPES = [QuestionType.Scale, QuestionType.OpenEnded, QuestionType.TextBox];

	public static readonly ENGAGEMENT_DIMENSION_ID = '52';
	public static readonly DIMENSION_SELECTOPTION_NAME_ENGAGEMENT = 'Engagement';
	public static readonly DIMENSION_SELECTOPTION_NAME_SHOW_ALL = 'SHOW_ALL';
	public static readonly DIMENSION_SELECTOPTION_NAME_NO_DIMENSION = '-1';

	public static readonly STATUS_DRAFT = 'Draft';
	public static readonly STATUS_SCHEDULING_IN_PROGRESS = 'Scheduling in Progress';
	public static readonly STATUS_SCHEDULED = 'Scheduled';

	public static readonly PACKAGE_INTERNAL = 'Internal';

	// FILTER
	public static readonly FILTER_PREFIX_TAG = 'TAG_';
	public static readonly FILTER_PREFIX_ROG = 'ROG_';
	public static readonly FILTER_PREFIX_QUESTIONTEMPLATE = 'QUESTIONTEMPLATE_';
	public static readonly FILTER_PREFIX_LANGUAGE = 'LANG_';
	public static readonly FILTER_PREFIX_BENCHMARK = 'BENCHMARK_';

	public static readonly DEFAULT_CULTURE_CODE = 'en-US';

	// DISPLAY LOGIC
	public static readonly DISPLAY_LOGIC_API = `${Constants.SURVEY_API_URL}/questions/display-logic`;

	// CUSTOM QUESTIONS
	public static readonly CUSTOMQUESTIONS_API = Constants.SURVEY_API_URL + '/custom-questions';
	public static readonly CUSTOMQUESTIONS_EDITMODEL_API = Constants.CUSTOMQUESTIONS_API + '/edit-models/{surveyQuestionId}';
	public static readonly CUSTOMQUESTION_RESPONSEOPTIONGROUP_CODES = [
		'1',
		'16',
		'19',
		'25',
		'20',
		'17',
		'21',
		'18',
		'22',
		'23',
		'24',
		'10',
		'2',
	];
	public static readonly DEMOGRAPHIC_RESPONSE_OPTION_TYPE = 5;
	public static readonly CUSTOMQUESTION_RESPONSEOPTIONGROUP_INCLUDED_TYPES = [
		ResponseOptionGroupType.TwoPointGroup,
		ResponseOptionGroupType.ThreePointGroup,
		ResponseOptionGroupType.SixPointGroup,
		ResponseOptionGroupType.FivePointGroup,
		ResponseOptionGroupType.ElevenPointGroup,
	];

	// DISTRIBUTION
	public static readonly DISTRIBUTION_LIST_DOWNLOAD = Constants.SURVEY_API_URL + '/audiences/{audienceId}/contacts/excel';
	public static readonly DISTRIBUTION_TEMPLATE_DOWNLOAD =
		Constants.API_URL + '/templates/distributionlist?distributionType={distributionType}&externalApplicationType={externalApplicationType}';
	public static readonly SHOW_ONLY_DUPLICATES_QUERY_STRING = '?showOnlyDuplicates=true';
	public static readonly DISTRIBUTION_CONTACT_EXPORT = Constants.SURVEY_API_URL + '/audiences/{audienceId}/contacts/export';
	public static readonly DISTRIBUTION_PENDING_PARTICIPANTS = Constants.SURVEY_API_URL + '/pending-contacts';

	// EMAIL FORMAT REGEX
	public static readonly EMAIL_ADDRESSES_LIMIT = 50;
	public static readonly EMAIL_CONTENT_LIMIT_IN_KB = 500;

	// SURVEY LINK
	public static readonly SURVEY_LINK_PLACEHOLDER = '{link to survey}';
	public static readonly FIRST_NAME_PLACEHOLDER = '{FirstName}';
	public static readonly LAST_NAME_PLACEHOLDER = '{LastName}';
	public static readonly EMAIL_ADDRESS_PLACEHOLDER = '{EmailAddress}';
	public static readonly ACCESS_CODE_PLACEHOLDER = '{AccessCode}';
	public static readonly SURVEY_LINK_BUTTON_ID = 'addSurveyLinkButton';
	public static readonly FIRST_NAME_BUTTON_ID = 'addFirstNameButton';
	public static readonly LAST_NAME_BUTTON_ID = 'addLastNameButton';
	public static readonly EMAIL_ADDRESS_BUTTON_ID = 'addEmailAddressButton';
	public static readonly ACCESS_CODE_BUTTON_ID = 'addAccessCodeButton';
	public static readonly SURVEY_LINK_REGEX = /\{link to survey\}/g;

	public static readonly excelResponseHeader = {
		Accept: 'application/vnd.openxmlformats-officedocument' + '.spreadsheetml.sheet',
	};
	public static readonly noCacheHeader = { Pragma: 'no-cache', 'Cache-Control': 'no-cache' };
	public static readonly noCacheConfig = { headers: Constants.noCacheHeader };
	public static readonly JsonContentTypeHeader = { 'Content-Type': 'application/json' };
	public static readonly JsonContentTypeConfig = { headers: Constants.JsonContentTypeHeader };
	public static readonly EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

	// EXTERNAL HELP LINK
	public static readonly HELP_SITE_URL = 'https://help.modernsurvey.com/PeoplePlatform.html';
	public static readonly CONTACT_SUPPORT_URL = 'https://www.msuitelink.com/support';

	// AUDIENCE DATE GAP (in hours)
	public static readonly OPEN_ON_GAP = 1;
	public static readonly REMINDER_SENDDATE_TO_CLOSEDATE_GAP = 1;

	// HIERARCHY
	public static readonly ORGMANAGER_API_BASE_URL = getStringFromConfig('REACT_APP_ORGMANAGER_API_BASE_URL');
	public static readonly ORGMANAGER_HIERARCHIES_URL = Constants.ORGMANAGER_API_BASE_URL + '/v1/hierarchies/';
	public static readonly HIERARCHY_ATTRIBUTES = 'attributes';
	public static readonly HIERARCHY_HRIS_SUMMARY = 'summary';
	public static readonly SURVEY_HIERARCHY_API_URL = Constants.API_URL + `/surveys/source`;

	// LOCAL STORAGE KEY
	public static readonly LOCAL_STORAGE_KEY_REPORTING_USERS = 'Reporting_Users';

	public static readonly DATE_FORMAT_LONG = 'MMM D, YYYY [at] hh:mma';

	public static customHtmlEditorOptions: object = {
		...CommonConstants.EDITOR_DEFAULT_SHARED,
		toolbar: [
			[
				'Bold',
				'Italic',
				'Underline',
				'BulletedList',
				'NumberedList',
				'Outdent',
				'Indent',
				'JustifyLeft',
				'JustifyCenter',
				'JustifyRight',
				'JustifyBlock',
				'FontSize',
				'Font',
				'RemoveFormat',
				'Undo',
				'Redo',
				'TextColor',
				'Link',
				'Unlink',
			],
		],
		removePlugins: 'elementspath,exportpdf,magicline,resize',
		versionCheck: false,
	};

	public static readonly KINCENTRIC_OWNER_ID = 1;

	public static readonly ORPHAN_PLACEHOLDER_ID: string = 'orphanPlaceHolder';

	// EXTERNAL APPLICATION TYPES - DISPLAY NAMES
	public static readonly MyReports = 'My Reports (eX-Pulse)';
	public static readonly mThrive = 'Thrive';
	public static readonly mExit = 'Exit';
	public static readonly mSpark = 'Spark';
	public static readonly mCulture = 'Culture';
	public static readonly mRisk = 'Risk';
	public static readonly mChange = 'Change';

	public static readonly FileDownloadResponseType = 'blob';
	public static readonly FileDownloadTimeout = 60 * 4 * 1000;
	public static readonly SurveyURLsFileName = 'Survey URLs.xlsx';

	public static readonly QUESTION_LIMIT: number = +(process.env.REACT_APP_QUESTION_LIMIT
		? getStringFromConfig('REACT_APP_QUESTION_LIMIT')
		: 1800);

	// AppDynamics
	public static APPDYNAMICS_APP_KEY = getStringFromConfig('REACT_APP_APPDYNAMICS_EUM_APP_ID');
	public static PENDO_APP_KEY = getStringFromConfig('REACT_APP_PENDO_APP_ID');

	// TEXT TO SPEECH
	public static readonly TEXTTOSPEECH_SUBSCRIPTION_KEY: string = '778fb993bb19418498c4dedfd4478185';
	public static readonly TEXTTOSPEECH_TOKEN_ISSUE_URL: string = 'https://westeurope.api.cognitive.microsoft.com/sts/v1.0/issueToken';
	public static readonly TEXTTOSPEECH_VOICE_LIST_URL: string = 'https://westeurope.tts.speech.microsoft.com/cognitiveservices/voices/list';
	public static readonly TextToSpeechHeader = {
		'Ocp-Apim-Subscription-Key': Constants.TEXTTOSPEECH_SUBSCRIPTION_KEY,
	};

	// QuestionsSummary
	public static readonly QUESTIONS: string = 'Questions';
	public static readonly PAGE_FLOW_ELEMENTS: string = 'PageFlowElements';

	public static readonly SYSTEM_CLIENT_ID: number = -1;

	public static readonly QuestionTypeOptions: ISelectOption[] = [
		{ name: QuestionType.Scale.toString(), displayText: ContentConstants.QUESTION_TYPE_RADIO_BUTTON, displayOrder: 1 },
		{ name: QuestionType.Slider.toString(), displayText: ContentConstants.QUESTION_TYPE_SLIDER, displayOrder: 2 },
	];

	public static readonly SampleQuestion = {
		isDemographic: false,
		questionType: QuestionType.Scale,
		text: '',
		code: '',
	};

	public static readonly RESPONSE_OPTION_GROUP_TAB_STANDARD: string = 'Standard';
	public static readonly RESPONSE_OPTION_GROUP_TAB_CUSTOM: string = 'Custom';
	public static readonly RESPONSE_OPTION_GROUP_TAB_HIERARCHY: string = 'Hierarchy';
	public static readonly RESPONSE_OPTION_GROUP_TAB_HIERARCHY_RESPONSE: string =
		'Upload or create a non-managerial hierarchy in Org Manager, then select it from the dropdown.';
	public static readonly RESPONSE_OPTION_GROUP_TAB_HIERARCHY_WARNING_MESSAGE: string =
		'Please save your hierarchy questions before moving to another tab.';

	public static readonly DEFAULT_THEME_NAME = 'Default';
	public static readonly CUSTOM_THEME_NAME = 'Custom';

	//SurveyContentItems Sort
	public static readonly PRIVACYPOLICY = 'PrivacyPolicy';
	public static readonly INSTRUCTIONS = 'Instructions';
	public static readonly DEFINITION = 'Definition';
}
